<template>
  <div class="mc-video-alert__wrapper">
    <template v-if="showTooltipLabelCamera">
      <span class="label-camera">
        <i class="icon-fill_videorecord" />
        {{ $t('trackVideo.camera') + ' ' + camera }}
      </span>
      <div class="background-gradient"></div>
    </template>
    <VideoConnecting v-if="isConnecting || $props?.isConnectingRequest" />
    <VideoShow
      v-else-if="mediaFilename && videoUrl"
      ref="videoShowRef"
      :key="`${mediaFilename}-${videoUrl}`"
      :mediaFilename="mediaFilename"
      :url="videoUrl"
      @play-state-change="$emit('play-state-change', $event)"
      @videoReady="onVideoReady"
    />
    <VideoUpload v-else-if="isVideoUploading" />
    <NoVideo v-else-if="isNoVideo" />
    <VideoRequest
      v-else
      :isDmsAdas="checkIsDmsAdas"
      :disabled="isMoreThan3Days"
      :isNewRequestUi="isNewRequestUi"
      @clickRequestVideo="clickRequestVideo"
    />
  </div>
</template>
<script>
import NoVideo from './state/NoVideo.vue'
import VideoShow from './state/VideoShow.vue'
import VideoUpload from './state/VideoUpload.vue'
import VideoRequest from './state/VideoRequest.vue'
import VideoConnecting from './state/VideoConnecting.vue'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import { SET_UPLOAD_MEDIA, UPDATE_FILELIST } from '@/services/api/onlineCommand'
import { MixPanel } from '@/mixin/mixpanel'
import { GET_VEHICLE_STATUSES_LAST_RECEIVE } from '@/services/api/fleet'
moment.locale('id')

export default {
  name: 'VideoAlert',
  components: { NoVideo, VideoShow, VideoUpload, VideoRequest, VideoConnecting },
  mixins: [Helper, MixPanel],
  props: {
    channel: {
      type: Number,
      required: true,
    },
    camera: {
      type: Number,
      required: true,
    },
    checkIsDmsAdas: {
      type: Boolean,
      required: true,
    },
    vehicleIdCam: {
      type: Number,
      required: true,
    },
    imeiCam: {
      type: String,
      required: true,
    },
    videoUrl: {
      type: String,
      required: false,
      default: '',
    },
    mediaFilename: {
      type: String,
      required: false,
      default: '',
    },
    statusDvh: {
      type: String,
      required: false,
      default: '',
    },
    rawAlertCreatedOn: {
      type: String,
      required: false,
      default: '',
    },
    deviceType: {
      type: String,
      required: true,
    },
    alertCode: {
      type: Number,
      required: true,
    },
    rawAlertId: {
      type: Number,
      required: true,
    },
    isConnectingRequest: {
      type: Boolean,
      required: false,
    },
    showTooltipLabelCamera: {
      type: Boolean,
      required: false,
      default: false,
    },
    isNewRequestUi: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDemo: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['startConnectingRequest', 'endConnectingRequest', 'play-state-change', 'noVideo'],
  data() {
    return {
      isConnecting: false,
      noVideoState: JSON.parse(localStorage.getItem('notification-no-video')) ?? [],
    }
  },
  computed: {
    isMoreThan3Days() {
      if (!this.rawAlertCreatedOn) return false
      return moment(this.rawAlertCreatedOn).isBefore(moment().subtract(3, 'days'))
    },
    isVideoUploading() {
      return this.statusDvh === 'I'
    },
    isNoVideo() {
      return this.noVideoState.some(
        (item) => item.rawAlertId === this.rawAlertId && item.channel === this.channel
      )
    },
  },
  methods: {
    restoreMediaRequestState({ isOfflineDevice = false, markNoVideo = false } = {}) {
      const hasExistingMedia = Boolean(this.videoUrl)

      if (markNoVideo && !hasExistingMedia) {
        this.setNoVideoStateToLocalStorage()
      }

      this.$store.dispatch('notification/setMediaPollingState', {
        id: this.rawAlertId,
        pollingStatus: hasExistingMedia ? 'complete' : 'timeout',
        canRequest: !hasExistingMedia,
        ...(hasExistingMedia ? {} : { videoData: null }),
        isOfflineDevice: hasExistingMedia ? false : isOfflineDevice,
      })
    },
    async checkDeviceOffline() {
      const lastReceive = await this.getLastReceive()
      const diffMinutes = this.getDurationBetween2Dates(lastReceive, this.now())
      return diffMinutes > 10
    },
    async getLastReceive() {
      const response = await this.$apollo.query({
        query: GET_VEHICLE_STATUSES_LAST_RECEIVE,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            vehicleId: [this.vehicleIdCam],
            vehicleStatus: ['service', 'archived', 'repair'],
          },
        },
      })
      return response.data.vehicleStatuses.vehicleStatuses?.[0]?.lastReceive
    },
    addListenerStartConnecting() {
      if (this.$listeners && !!this.$listeners['startConnectingRequest']) {
        this.$emit('startConnectingRequest', this.vehicleIdCam, this.channel)
      }
    },
    addListenerEndConnecting() {
      if (this.$listeners && !!this.$listeners['endConnectingRequest']) {
        this.$emit('endConnectingRequest', this.vehicleIdCam, this.channel)
      }
    },
    async clickRequestVideo() {
      const detailNotif = this.$store.state.notification.detailNotification

      if (detailNotif.isDemo && !detailNotif.isDmsAdas) {
        this.isConnecting = true
        this.addListenerStartConnecting()
        this.$store.dispatch('notification/setMediaPollingState', {
          id: this.rawAlertId,
          pollingStatus: 'requesting',
        })
        await this.delay(1000)
        let listNotificationMedia = this.$store.state.notification.listNotificationMedia

        const media = listNotificationMedia.find(
          (item) => item.identifier === 'V' && item.channel === Number(this.channel)
        )

        this.isConnecting = false
        this.addListenerEndConnecting()
        this.$store.commit('notification/addDataDvhRequested', {
          ...media,
          url: '',
          status: 'I',
          rawAlertId: this.rawAlertId,
        })
        this.$store.dispatch('notification/setMediaPollingState', {
          id: this.rawAlertId,
          pollingStatus: 'checking',
        })

        await this.delay(5000)

        const successMedia = {
          ...media,
          url:
            process.env.VUE_APP_DEMO_ASSET_URL +
            '/' +
            this.vehicleIdCam +
            '/' +
            media.mediaFilename,
          status: 'S',
        }

        this.$store.commit('notification/updateDataDvhRequested', {
          downloadId: media.downloadId,
          data: successMedia,
        })

        this.$store.commit('notification/setListNotificationMedia', [...listNotificationMedia])

        if (successMedia?.url) {
          this.$store.dispatch('notification/setMediaPollingState', {
            id: this.rawAlertId,
            pollingStatus: 'complete',
            canCheck: false,
            canRequest: false,
            videoData: successMedia,
          })
        }
        return
      }
      try {
        this.track('Request video capture alert')
        this.isConnecting = true
        this.addListenerStartConnecting()
        this.$store.dispatch('notification/setMediaPollingState', {
          id: this.rawAlertId,
          pollingStatus: 'requesting',
        })
        const isDeviceOffline = await this.checkDeviceOffline()

        if (isDeviceOffline) {
          this.$mcToast.warning(this.$t('notification.text.deviceUnreachable'))
          this.restoreMediaRequestState({ isOfflineDevice: true })
          this.addListenerEndConnecting()
          this.isConnecting = false
          return
        }

        const startTime = moment(this.rawAlertCreatedOn)
          .clone()
          .subtract(5, 'seconds')
          .toISOString()
        const endTime = moment(this.rawAlertCreatedOn).clone().add(5, 'seconds').toISOString()
        const input = {
          imei: this.imeiCam,
          rawAlertCode: this.alertCode,
          channel: this.channel,
          startTime,
          endTime,
          rawAlertCreatedOn: this.rawAlertCreatedOn,
          isEvent: true,
          useInMemoryDB: true,
        }

        await this.updateFileList()
        const delay = 5000
        await this.delay(delay)

        const response = await this.$apollo.mutate({
          mutation: SET_UPLOAD_MEDIA,
          variables: {
            input,
          },
        })

        const responseMessage = response.data.setUploadMedia.setUploadMedia.message
        const dataDvh = response.data.setUploadMedia.setUploadMedia.data
        if (responseMessage === 'Not Sent(No video downloaded)') {
          this.$mcToast.error(this.$t('notification.text.videoNotFound'))
          this.restoreMediaRequestState({ markNoVideo: true })
          this.isConnecting = false
          this.addListenerEndConnecting()
          return
        }

        if (response.data.setUploadMedia.isSuccessful && responseMessage === 'Sent') {
          dataDvh.forEach((data) => {
            this.$store.commit('notification/addDataDvhRequested', {
              ...data,
              rawAlertId: this.rawAlertId,
            })
          })
          this.$store.dispatch('notification/setMediaPollingState', {
            id: this.rawAlertId,
            pollingStatus: 'checking',
          })
          this.isConnecting = false
          this.addListenerEndConnecting()
        } else {
          throw new Error('setUploadMedia not successfull')
        }
      } catch (error) {
        console.error(error)
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        this.restoreMediaRequestState()
        this.isConnecting = false
        this.addListenerEndConnecting()
      }
    },
    async updateFileList() {
      const input = {
        imei: this.imeiCam,
        useInMemoryDB: true,
      }
      if (this.deviceType === 'bsj' || this.deviceType === 'vss-howen') {
        const dateRange = this.getDefaultUpdateFileListAlertDateRange(this.rawAlertCreatedOn)
        input['channel'] = this.channel
        input['dateRange'] = {
          start: dateRange.start,
          end: dateRange.end,
        }
      }
      await this.$apollo.mutate({
        mutation: UPDATE_FILELIST,
        variables: {
          input,
        },
        fetchPolicy: 'no-cache',
      })
    },

    setNoVideoStateToLocalStorage() {
      const newArrNoVideoState = [
        ...this.noVideoState,
        { rawAlertId: this.rawAlertId, channel: this.channel },
      ]
      localStorage.setItem('notification-no-video', JSON.stringify(newArrNoVideoState))
      this.noVideoState = newArrNoVideoState
      this.$emit('no-video', { rawAlertId: this.rawAlertId, channel: this.channel })
    },
    onVideoReady() {
      this.$emit('videoReady')
    },
  },
}
</script>
<style lang="scss">
.mc-alert-center-detail__media-wrapper.media-detail {
  .mc-video-alert__video-item-state {
    height: 256px;
  }
  .mc-video-alert__wrapper {
    height: 256px;
    .video-js {
      height: 256px;
    }
  }
}
.mc-video-alert__video-item-state {
  background-color: $blue-900;
  border-radius: 8px;
  height: 390px;

  * {
    color: $white;
  }

  .mc-video-alert__content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
}

.mc-video-alert__wrapper {
  position: relative;
  &:hover {
    .label-camera {
      display: flex;
    }
    .background-gradient {
      display: block;
    }
  }

  .background-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 150px;
    height: 50px;
    background: #000000;
    display: none;
    border-radius: 10px 0 0 0;
    z-index: 2;
    background: #000000;
    background: linear-gradient(163deg, rgba(0, 0, 0, 1) 14%, rgba(255, 255, 255, 0) 42%);
  }

  .label-camera {
    display: none;
    position: absolute;
    color: $white;
    z-index: 3;
    top: 6px;
    left: 10px;
    align-items: center;
    column-gap: 4px;
  }
}

.mc-notification-media__icon-play-video {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: $white;
  font-size: 80px;
  opacity: 0.8;
}

.mc-notification-media__item--show {
  cursor: pointer;
  position: relative;
}

.mc-notification-media__item--show {
  position: relative;

  &:hover {
    .mc-notification-media__action {
      opacity: 1;
    }
  }

  .video-js {
    width: 100%;
    height: 390px;
    border-radius: 10px;
  }
}

.mc-notification-media__action {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  padding: 10px;
  box-sizing: border-box;
  background-color: #344264a3;
  z-index: 2;
  color: $white;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  opacity: 0;
  transition: 0.3s;
  .mc-notification-media__loading-state {
    cursor: not-allowed;
    display: flex;
    align-items: center;
    column-gap: 4px;
    color: $teal-200;
  }
  span {
    cursor: pointer;
  }
  .mc-fa {
    &:before {
      color: $white;
    }

    &.icon-outline_download {
      margin-right: 5px;
    }
  }
}

.mc-notification-media__icon-play-video {
  position: absolute;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 80px;
  opacity: 0.8;
}

.mc-notification-media-video__fullscreen {
  z-index: 99999;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  .vjs-custom-skin {
    width: 100%;
    height: 100%;
    .video-js {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
    video {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      object-fit: contain;
      background-color: $blue-900;
    }
  }

  .mc-notification-media__action {
    position: fixed;
  }
  .icon-fill_fullscreen {
    display: none;
  }
  .icon-fill_exit-fullscreen {
    display: block;
  }
}
</style>
